import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AlertSeverity } from "../../../models/common/enums";
import { isEqual } from "lodash";
import { ITypedOption } from "../../../models/common/typedOptions";
import {
    createAsyncThunk,
    createEntityAdapter,
    EntityState,
} from "@reduxjs/toolkit";
import { getOwnerOptions } from "../../../services/optionService";
import { ICodeTitle } from "../../../models/common/codeTitle";
import { getTimezones } from "../../../services/timeZoneService";

export enum AlertType {
    VehicleStopped = "VehicleStopped",
    AppUpdate = "AppUpdate",
}

export interface IAlertState {
    severity?: AlertSeverity;
    message: string;
    duration?: number;
    debounce?: boolean;
    type?: AlertType;
    dialog?: boolean;
    onClick?: () => void;
}

export interface AppOption {
    code: string;
}

export interface AppOptionsState {
    loading: boolean;
}

export enum WorkTimeTypeDrawerState {
    PendingOpen = "PendingOpen",
    PendingClose = "PendingClose",
    None = "None"
}

export enum AppUpdateState {
    None = "None",
    Pending = "Pending",
    Confirmed = "Confirmed"
}

export interface AppState {
    alerts: IAlertState[];
    options: EntityState<ITypedOption, string> & AppOptionsState;
    timeZones: ICodeTitle[];
    drawer: WorkTimeTypeDrawerState;
    update: AppUpdateState;
}

const optionAdapter = createEntityAdapter<ITypedOption, string>({
    selectId: (option) => option.code.toLowerCase(),
});

const initialState: AppState = {
    alerts: [],
    options: optionAdapter.getInitialState({ loading: true }),
    timeZones: null,
    drawer: WorkTimeTypeDrawerState.None,
    update: AppUpdateState.None,
};

export const appSlice = createSlice({
    name: "app",
    initialState,
    reducers: {
        addAlert: (state, action: PayloadAction<IAlertState>) => {
            // Remove existing permanent (duration === null) alerts
            // to avoid stacking multiple alerts.
            state.alerts = state.alerts.filter((a) => a.duration !== null);

            if (
                !action.payload.debounce ||
                !state.alerts.find((a) => isEqual(a, action.payload))
            ) {
                state.alerts = [...state.alerts, action.payload];
            }
        },
        removeAlert: (state) => {
            state.alerts.splice(0, 1);
        },
        deleteAlertByType: (state, action: PayloadAction<AlertType>) => {
            state.alerts = state.alerts.filter(
                (a) => a.type !== action.payload
            );
        },
        setAppOptions: (state, action: PayloadAction<ITypedOption[]>) => {
            optionAdapter.setAll(state.options, action.payload);
            state.options.loading = false;
        },
        setTimeZones: (state, action: PayloadAction<ICodeTitle[]>) => {
            state.timeZones = action.payload;
        },
        clearStore: () => {
            // This is handled by rootReducer
        },
        toggleWorkTimeTypeDrawer: (state, action: PayloadAction<WorkTimeTypeDrawerState>) => {
            state.drawer = action.payload;
        },
        setAppUpdate: (state, action: PayloadAction<AppUpdateState>) => {
            state.update = action.payload;
        },
    },
});

export const {
    addAlert,
    removeAlert,
    deleteAlertByType,
    setAppOptions,
    setTimeZones,
    clearStore,
    toggleWorkTimeTypeDrawer,
    setAppUpdate,
} = appSlice.actions;

export const addErrorMsg = (msg: IAlertState) =>
    addAlert({ ...msg, severity: AlertSeverity.Error });

export const fetchAppOptions = createAsyncThunk(
    "app/fetchOptions",
    async(_, thunkApi) => {
        const options = await getOwnerOptions(thunkApi.signal);
        thunkApi.dispatch(setAppOptions(options));
    }
);

export const fetchTimeZones = createAsyncThunk(
    "app/fetchTimeZones",
    async(_, thunkApi) => {
        const timeZones = await getTimezones(thunkApi.signal);
        thunkApi.dispatch(setTimeZones(timeZones));
    }
);

export const addSuccessMsg = (message: string, duration?: number) =>
    addAlert({ message, severity: AlertSeverity.Success, duration });

export const appReducer = appSlice.reducer;

export const appOptionSelectors = optionAdapter.getSelectors();
